// franklycatering #1557 — the Events page's view (Pipeline or List), filters
// and active quick view. ?view= and ?incomplete=1 win; otherwise the person's
// last view and filters come back from localStorage on this device.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { eventsViewFrom, type EventsView } from "@/lib/event-pipeline";
import {
  QUICK_VIEWS,
  defaultFilters,
  incompleteFilters,
  isDefaultFilters,
  parseStoredView,
  storageKey,
  toStoredView,
  type EventFilters,
  type QuickViewKey,
} from "@/lib/event-filters";

interface ViewState {
  /** The storage key this state was loaded for; a different signed-in user reloads. */
  key: string | null;
  view: EventsView;
  filters: EventFilters;
  quick: QuickViewKey | null;
}

function readStored(key: string | null, today: Date) {
  if (!key) return null;
  try {
    return parseStoredView(localStorage.getItem(key), today);
  } catch {
    // Storage blocked (private mode, a browser policy): start from the defaults.
    return null;
  }
}

function initialState(key: string | null, params: URLSearchParams, today: Date): ViewState {
  const stored = readStored(key, today);
  const incomplete = params.get("incomplete") === "1";
  return {
    key,
    view: eventsViewFrom(params, stored?.view ?? "pipeline"),
    filters: incomplete ? incompleteFilters(today) : (stored?.filters ?? defaultFilters(today)),
    quick: null,
  };
}

export interface EventsViewState {
  view: EventsView;
  setView: (v: EventsView) => void;
  filters: EventFilters;
  /** Changes some filters; any change ends the active quick view. */
  updateFilters: (patch: Partial<EventFilters>) => void;
  /** Back to the defaults ("Clear filters"). */
  clearFilters: () => void;
  quick: QuickViewKey | null;
  /** Applies a quick view, or resets to the defaults when it is already on. */
  applyQuickView: (key: QuickViewKey) => void;
  /** The "Incomplete leads ×" chip: back to the saved (or default) filters. */
  clearIncomplete: () => void;
  isDefault: boolean;
}

export function useEventsViewState(today: Date): EventsViewState {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const key = user ? storageKey(user.id) : null;
  const [state, setState] = useState<ViewState>(() => initialState(key, params, today));
  const view = eventsViewFrom(params, state.view);

  // A different person signed in while the page stayed mounted: load theirs.
  useEffect(() => {
    if (state.key !== key) setState(initialState(key, params, today));
  }, [state.key, key, params, today]);

  // Nothing is saved while ?incomplete=1 is on: those filters are the link's, not the person's.
  useEffect(() => {
    if (!key || state.key !== key || state.filters.incomplete) return;
    try {
      localStorage.setItem(key, JSON.stringify(toStoredView(view, state.filters)));
    } catch {
      // Storage blocked or full: the page still works, it just won't remember the view.
    }
  }, [key, state.key, state.filters, view]);

  const dropIncompleteParam = useCallback(() => {
    setParams(
      (prev) => {
        if (!prev.has("incomplete")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("incomplete");
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  const setView = useCallback(
    (v: EventsView) => {
      setState((s) => ({ ...s, view: v }));
      setParams({ view: v }, { replace: true });
    },
    [setParams],
  );

  const updateFilters = useCallback((patch: Partial<EventFilters>) => {
    setState((s) => ({ ...s, filters: { ...s.filters, ...patch }, quick: null }));
  }, []);

  const clearFilters = useCallback(() => {
    setState((s) => ({ ...s, filters: defaultFilters(today), quick: null }));
    dropIncompleteParam();
  }, [today, dropIncompleteParam]);

  const clearIncomplete = useCallback(() => {
    setState((s) => ({ ...s, filters: readStored(s.key, today)?.filters ?? defaultFilters(today), quick: null }));
    dropIncompleteParam();
  }, [today, dropIncompleteParam]);

  const applyQuickView = useCallback(
    (k: QuickViewKey) => {
      setState((s) => {
        if (s.quick === k) return { ...s, filters: defaultFilters(today), quick: null };
        const qv = QUICK_VIEWS.find((v) => v.key === k);
        return qv ? { ...s, filters: qv.filters(today), quick: k } : s;
      });
    },
    [today],
  );

  const isDefault = useMemo(() => isDefaultFilters(state.filters, today), [state.filters, today]);

  return useMemo(
    () => ({
      view,
      setView,
      filters: state.filters,
      updateFilters,
      clearFilters,
      quick: state.quick,
      applyQuickView,
      clearIncomplete,
      isDefault,
    }),
    [view, setView, state.filters, updateFilters, clearFilters, state.quick, applyQuickView, clearIncomplete, isDefault],
  );
}
